import { useEffect, useState } from 'react';

interface Props {
  delay?: number;
  enabled?: boolean;
}

interface ReturnProps {
  isScrolling: boolean;
  canScrollVertically: boolean;
  hasVerticalScrolling: boolean;
}

const BOTTOM_THRESHOLD_PX = 10;

const useScrollDetection = ({ delay = 200, enabled = true }: Props): ReturnProps => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [hasVerticalScrolling, setHasVerticalScrolling] = useState(false);
  const [canScrollVertically, setCanScrollVertically] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    let timeoutId: NodeJS.Timeout | undefined;

    const syncScrollState = () => {
      const { scrollHeight } = window.document.body;
      const isPageTallerThanViewport = scrollHeight > window.innerHeight;
      setHasVerticalScrolling(isPageTallerThanViewport);
      setCanScrollVertically(
        isPageTallerThanViewport && window.scrollY + window.innerHeight + BOTTOM_THRESHOLD_PX < scrollHeight,
      );
    };

    // Measuring only at gesture start and end keeps scroll events free of layout reads.
    const handleScroll = () => {
      setIsScrolling(true);
      if (timeoutId === undefined) syncScrollState();

      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        timeoutId = undefined;
        setIsScrolling(false);
        syncScrollState();
      }, delay);
    };

    // Measure once after the first layout to avoid a forced reflow during hydration; later updates come from scroll only.
    const bodyResizeObserver = new ResizeObserver(() => {
      bodyResizeObserver.disconnect();
      syncScrollState();
    });
    bodyResizeObserver.observe(window.document.body);
    window.addEventListener('scroll', handleScroll);

    return () => {
      bodyResizeObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [enabled, delay]);

  return { isScrolling, hasVerticalScrolling, canScrollVertically };
};

export default useScrollDetection;
